import {
  collection,
  getDoc,
  getDocs,
  addDoc,
  doc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  query,
  where,
  documentId,
} from "firebase/firestore";
import { db } from "./firebase";
import { getAvatarUrl } from "../hooks/useAvatarUtils";

// This file is the app's Firestore data-access layer: every read/write to
// the "events", "communities", and "users" (matches) collections goes
// through here, so components never call Firestore directly. Each function
// swallows its own errors with console.error

/**
 * Fetch multiple user documents by ID in as few round-trips as possible.
 * Firestore's "in" operator supports at most 30 values per query, so ids
 * are split into chunks and the chunk queries run in parallel.
 * Returns a Map<userId, userData> (missing/deleted users are simply absent).
 */
export async function fetchUsersByIds(userIds) {
  const uniqueIds = [...new Set((userIds || []).filter(Boolean))];
  const usersMap = new Map();
  if (uniqueIds.length === 0) return usersMap;

  const CHUNK_SIZE = 30;
  const chunks = [];
  for (let i = 0; i < uniqueIds.length; i += CHUNK_SIZE) {
    chunks.push(uniqueIds.slice(i, i + CHUNK_SIZE));
  }

  try {
    const snapshots = await Promise.all(
      chunks.map((chunk) =>
        getDocs(
          query(collection(db, "users"), where(documentId(), "in", chunk))
        )
      )
    );
    snapshots.forEach((snapshot) => {
      snapshot.forEach((userDoc) => {
        usersMap.set(userDoc.id, userDoc.data());
      });
    });
  } catch (error) {
    console.error("Error batch-fetching users by id:", error);
  }

  return usersMap;
}

// --- EVENTS ---

// Fetch all events from Firestore
export async function fetchAllEvents() {
  try {
    const querySnapshot = await getDocs(collection(db, "events"));
    const events = [];
    querySnapshot.forEach((doc) => {
      events.push({ id: doc.id, ...doc.data() });
    });
    return events;
  } catch (error) {
    console.error("Error fetching events:", error);
    return [];
  }
}

// Create a new event in Firestore
export async function createEventInFirestore(eventData) {
  try {
    const docRef = await addDoc(collection(db, "events"), eventData);
    return { id: docRef.id, ...eventData };
  } catch (error) {
    console.error("Error creating event:", error);
    throw error;
  }
}

// Sync event attendance for both user and event documents
// Attendance is tracked on BOTH sides so each can be queried independently:
// the user's document lists which events they joined (for "my events" UI),
// and the event's document lists its attendees (for headcounts/invite
// screens). arrayUnion/arrayRemove keep both writes idempotent.
export async function toggleEventAttendance(userId, eventId, isJoining) {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      joinedEvents: isJoining ? arrayUnion(eventId) : arrayRemove(eventId),
    });

    const eventRef = doc(db, "events", eventId);
    await updateDoc(eventRef, {
      attendees: isJoining ? arrayUnion(userId) : arrayRemove(userId),
    });
  } catch (error) {
    console.error("Error updating event attendance:", error);
    // Re-thrown (unlike the other functions in this file) so callers doing
    // an optimistic local update — see handleToggleJoinEvent in
    // useAppLogic.js — know to roll it back instead of silently drifting
    // out of sync with Firestore.
    throw error;
  }
}

// --- COMMUNITIES ---

// Fetch all communities from Firestore
export async function fetchAllCommunities() {
  try {
    const querySnapshot = await getDocs(collection(db, "communities"));
    const communities = [];
    querySnapshot.forEach((doc) => {
      communities.push({ id: doc.id, ...doc.data() });
    });
    return communities;
  } catch (error) {
    console.error("Error fetching communities:", error);
    return [];
  }
}

// Create a new community in Firestore
export async function createCommunity(communityData) {
  try {
    const docRef = await addDoc(collection(db, "communities"), communityData);
    return { id: docRef.id, ...communityData };
  } catch (error) {
    console.error("Error creating community:", error);
    throw error;
  }
}

// Sync community membership for both user and community documents
// Same dual-write reasoning as toggleEventAttendance above: the user's
// joinedCommunities list and the community's members list are kept in sync
// so either can be queried without reading the other.
export async function toggleCommunityMembership(
  userId,
  communityId,
  isJoining
) {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      joinedCommunities: isJoining
        ? arrayUnion(communityId)
        : arrayRemove(communityId),
    });

    const communityRef = doc(db, "communities", communityId);
    await updateDoc(communityRef, {
      members: isJoining ? arrayUnion(userId) : arrayRemove(userId),
    });
  } catch (error) {
    console.error("Error updating community membership:", error);
    throw error;
  }
}

// --- CHATS ---
// Every chat between two users lives at chats/{sharedChatId}, where
// sharedChatId is deterministic (both users compute the same value without
// needing to look anything up first). This used to be split across two
// disconnected schemes — an auto-id "chats" doc created on invite-accept
// (for the "participants array-contains" listing query) and a completely
// different deterministic-id doc that actual messages were written under —
// which meant lastMessage/updatedAt on the listing doc could never reflect
// real conversation activity. Keeping everything under one id fixes that.

/**
 * The single canonical chat id for a pair of users, independent of who
 * looks it up or in what order.
 */
export function getSharedChatId(user1Id, user2Id) {
  if (!user1Id || !user2Id) return "";
  return [user1Id, user2Id].sort().join("_");
}

/**
 * Ensure a chats/{sharedChatId} document exists for this pair of users,
 * with participants set and lastMessage/updatedAt refreshed. Safe to call
 * repeatedly (e.g. on every invite-accept or message send) since setDoc
 * with merge:true only touches the fields passed in.
 */
export async function getOrCreateChat(userId1, userId2, lastMessage) {
  const chatId = getSharedChatId(userId1, userId2);
  if (!chatId) throw new Error("getOrCreateChat: missing user id(s)");

  const chatRef = doc(db, "chats", chatId);
  await setDoc(
    chatRef,
    {
      participants: [userId1, userId2],
      ...(lastMessage ? { lastMessage, updatedAt: serverTimestamp() } : {}),
    },
    { merge: true }
  );

  return chatId;
}

/**
 * Refresh a chat's preview fields after a new message is sent, so the chat
 * list (ChatScreen, Profile connections) reflects real activity instead of
 * staying frozen at whatever text was set when the chat was first created.
 */
export async function updateChatLastMessage(chatId, lastMessage) {
  const chatRef = doc(db, "chats", chatId);
  await setDoc(
    chatRef,
    { lastMessage, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

// --- MATCHES / CONNECTIONS ---

// Save a matched user inside the user document as an array
// Matches are stored as a plain array on the user document (not a
// subcollection), keyed by matchId. Note: arrayUnion compares elements by
// deep equality, so if a match object is ever re-saved with a changed
// field (e.g. updatedAt below), it will be added as a second entry rather
// than replacing the first — callers should dedupe by id before calling
// this (see handleAddMatch in useAppLogic.js).
export async function saveMatchToFirestore(userId, matchedUser) {
  try {
    const matchId = String(matchedUser.id || matchedUser.uid);
    const formattedMatch = {
      ...matchedUser,
      id: matchId,
      uid: matchId,
      updatedAt: new Date().toISOString(),
    };

    const userRef = doc(db, "users", userId);

    // Usamos arrayUnion para añadir la conexión al array "matches" del documento principal
    await updateDoc(userRef, {
      matches: arrayUnion(formattedMatch),
    });
  } catch (error) {
    console.error("Error saving match:", error);
  }
}

// --- INVITATIONS ---

/**
 * Create a single "invitations" document inviting targetUser to event, sent
 * by the current session's user (senderData). Shared by useEvent.js (the
 * event details screen) and InviteUser.jsx (the swipeable invite screen) so
 * both send the exact same document shape.
 */
export async function sendEventInvitation(
  senderData,
  senderUid,
  targetUser,
  event
) {
  const targetId = String(targetUser.id || targetUser.uid);

  await addDoc(collection(db, "invitations"), {
    senderId: senderUid,
    senderName: senderData?.name || "Someone",
    senderAvatar: getAvatarUrl(senderData),
    receiverId: targetId,
    eventId: event.id,
    eventTitle: event.title,
    status: "pending",
    createdAt: serverTimestamp(),
  });
}

// Fetch all matches directly from the user document
export async function fetchUserMatches(userId) {
  try {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const data = userSnap.data();
      return data.matches || [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching matches:", error);
    return [];
  }
}
